import { Plus } from 'lucide-react'
import { useState, type ReactNode, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPatch, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { PlanFields } from './plan/PlanFields'
import { emptyDraft, requiredProblems, type PlanDraft } from './plan/model'
import { savePlan } from './plan/api'
import { PROJECT_TYPE_LABEL } from '@/components/composites/status-badge'
import { UserAvatar } from '@/components/composites/user-avatar'
import { flattenTeams } from '@/features/teams'
import { SearchSelect } from '@/components/composites/search-select'
import type { Client, Geography,
  GeographyMode, Project, ProjectTypeKey, TeamNode } from '@/types/entities'
import { PROJECT_TYPES, clientRuleHint } from './projectTypes'
import { VisibilityField } from '@/features/work/VisibilityField'
import { SourceLinksField, cleanSourceLinks } from '@/components/composites/source-links-field'
import { AttachmentsField, cleanAttachments, type AttachmentRef } from '@/components/composites/attachments-field'
import { AttachmentList } from '@/components/composites/attachment-list'
import { ClientDialog } from '@/features/clients/ClientDialog'
import { StateHistory, type StateHistoryPayload } from '@/components/composites/state-history'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { deleteLocalDraft, newDraftId, saveLocalDraft } from '@/features/drafts/draftStore'

/** Selects cannot hold an empty-string value, so "none" gets a sentinel. */
const NONE = 'none'

export interface ProjectDraftPayload {
  name: string
  type: ProjectTypeKey | ''
  clientId: string
  geographyId: string
  teamIds: string[]
  ownerId: string
  description: string
  sourceLinks: string[]
  attachments: AttachmentRef[]
  visibleToUserIds: string[]
  visibleToTeamIds: string[]
}

/**
 * Open or edit a project.
 *
 * One dialog for both, because the fields are identical and two of them would drift. The
 * client rule is shown as guidance while typing — the backend is the authority and its
 * message is what gets surfaced if the save is refused.
 */
export function ProjectDialog({
  project,
  clients,
  members,
  trigger,
  open: openProp,
  onOpenChange,
  onCreated,
  draftId: draftIdProp,
  draftPayload,
  onDraftClosed,
  createTypes,
  initialType,
}: {
  /** Absent = create. Present = edit that project. */
  project?: Project
  clients: Client[]
  members: Array<{ id: string; displayName: string; photoUrl?: string | null }>
  /** Omit when opening this from somewhere else — see `open`. */
  trigger?: ReactNode
  /**
   * Controlled, for opening this on top of another form.
   *
   * Somebody filling in a form who has no project to file against should not have to abandon
   * what they typed and go to another tab. `onCreated` hands the new project straight back,
   * so it is already selected when this closes.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onCreated?: (project: Project) => void
  draftId?: string
  draftPayload?: ProjectDraftPayload
  onDraftClosed?: () => void
  /** Creating: the types offered, and the one preset. Editing always offers every type. */
  createTypes?: ProjectTypeKey[]
  initialType?: ProjectTypeKey
}) {
  const queryClient = useQueryClient()
  const { userId } = useIdentity()
  const { workspaceId } = useWorkspace()
  const [draftId, setDraftId] = useState(() => draftIdProp ?? newDraftId())
  const [openState, setOpenState] = useState(Boolean(draftPayload))
  const open = openProp ?? openState
  const setOpen = onOpenChange ?? setOpenState
  const [tried, setTried] = useState(false)
  const [creatingClient, setCreatingClient] = useState(false)
  const [sourceLinks, setSourceLinks] = useState<string[]>(project?.sourceLinks ?? draftPayload?.sourceLinks ?? [])
  /*
   * Seeded from what the project already carries.
   *
   * The API reads the submitted list as the whole truth, so an edit that never touched this
   * field must still send the files that were already there — starting empty would take an
   * untouched form as "remove them all".
   */
  const [attachments, setAttachments] = useState<AttachmentRef[]>(
    () => project?.attachments?.map((a) => ({ key: a.key, name: a.name })) ?? draftPayload?.attachments ?? [],
  )
  const [visibleToUserIds, setVisibleToUserIds] = useState<string[]>(project?.visibleToUserIds ?? draftPayload?.visibleToUserIds ?? [])
  const [visibleToTeamIds, setVisibleToTeamIds] = useState<string[]>(project?.visibleToTeamIds ?? draftPayload?.visibleToTeamIds ?? [])

  const [name, setName] = useState(project?.name ?? draftPayload?.name ?? '')
  const [type, setType] = useState<ProjectTypeKey | ''>(project?.type ?? draftPayload?.type ?? initialType ?? '')
  const [clientId, setClientId] = useState(project?.clientId ?? draftPayload?.clientId ?? NONE)
  const [geographyId, setGeographyId] = useState(project?.geographyId ?? draftPayload?.geographyId ?? NONE)
  /** The teams delivering it. Work filed here may only name one of these. */
  const [teamIds, setTeamIds] = useState<string[]>(project?.teamIds ?? draftPayload?.teamIds ?? [])
  const [ownerId, setOwnerId] = useState(project?.ownerId ?? draftPayload?.ownerId ?? NONE)
  const [description, setDescription] = useState(project?.description ?? draftPayload?.description ?? '')
  const [plan, setPlan] = useState<PlanDraft>(() => emptyDraft())
  const [step, setStep] = useState(0)
  /** Creating: the first step is three short pages. Editing shows everything at once. */
  const [sub, setSub] = useState(0)
  const showSub = (n: number) => Boolean(project) || sub === n
  const [planTried, setPlanTried] = useState(false)

  const hasClient = clientId !== NONE

  // A geography belongs to one client, so the list is fetched per client rather than
  // filtered from a global one — the backend already narrows it.
  const historyQuery = useQuery({
    queryKey: ['project', project?.id ?? '', 'state-history'],
    queryFn: () => apiGet<StateHistoryPayload>(`projects/${project!.id}/state-history`),
    enabled: open && Boolean(project),
  })

  const teamsQuery = useQuery({
    queryKey: qk.teamTree(),
    queryFn: () => apiGet<{ tree: TeamNode[] }>('teams', { tree: true }),
    enabled: open,
    staleTime: 5 * 60_000,
  })
  const teams = useMemo(() => flattenTeams(teamsQuery.data?.tree ?? []), [teamsQuery.data])

  const geographiesQuery = useQuery({
    queryKey: qk.geographies(hasClient ? clientId : undefined),
    queryFn: () =>
      apiGet<{ items: Geography[] }>('geographies', { clientId }).then((r) => r.items),
    enabled: open && hasClient,
    staleTime: 60_000,
  })
  const geographies = geographiesQuery.data ?? []

  const [addingGeography, setAddingGeography] = useState(false)
  const [geoName, setGeoName] = useState('')
  const [geoMode, setGeoMode] = useState<GeographyMode>('live')
  const [geoAddress, setGeoAddress] = useState('')

  const addGeography = useMutation({
    mutationFn: () =>
      apiPost<Geography>('geographies', {
        clientId,
        name: geoName.trim(),
        mode: geoMode,
        // The API refuses an address on a virtual geography, so do not send one.
        address: geoMode === 'live' ? geoAddress.trim() || null : null,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['geographies'] })
      // Select what was just made — adding it and then having to find it is a pointless step.
      setGeographyId(created.id)
      setAddingGeography(false)
      setGeoName('')
      setGeoAddress('')
      setGeoMode('live')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const reset = () => {
    setTried(false)
    setName(project?.name ?? '')
    setType(project?.type ?? '')
    setClientId(project?.clientId ?? NONE)
    setGeographyId(project?.geographyId ?? NONE)
    setTeamIds(project?.teamIds ?? [])
    setOwnerId(project?.ownerId ?? NONE)
    setDescription(project?.description ?? '')
    setPlan(emptyDraft())
    setStep(0)
    setSub(0)
    setPlanTried(false)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        type,
        clientId: hasClient ? clientId : null,
        geographyId: hasClient && geographyId !== NONE ? geographyId : null,
        teamIds,
        sourceLinks: cleanSourceLinks(sourceLinks),
        attachments: cleanAttachments(attachments),
        visibleToUserIds,
        visibleToTeamIds,
        description: description.trim(),
        ownerId: ownerId === NONE ? null : ownerId,
      }
      return project
        ? apiPatch<Project>(`projects/${project.id}`, { ...body, version: project.version })
        : apiPost<Project>('projects', body)
    },
    onSuccess: (saved) => {
      toast.success(project ? `Saved “${saved.name}”` : `Opened “${saved.name}”`)
      // The plan is a second write; the project already exists, so a failure here is a warning, not a lost project.
      if (!project) {
        void savePlan(saved.id, plan, null)
          .then(() => queryClient.invalidateQueries({ queryKey: ['project', saved.id, 'plan'] }))
          .catch((error) => toast.warning(`Project opened, but its plan was not saved: ${getErrorMessage(error)}. Add it from the project page.`))
      }
      // Hand it back so whoever opened this has it selected already — the same courtesy the
      // geography field inside this dialog already does.
      if (!project) onCreated?.(saved)
      if (!project && userId) deleteLocalDraft({ id: draftId, kind: 'project', userId, workspaceId })
      onDraftClosed?.()
      void queryClient.invalidateQueries({ queryKey: qk.projects() })
      void queryClient.invalidateQueries({ queryKey: qk.project(saved.id) })
      void queryClient.invalidateQueries({ queryKey: ['project', saved.id, 'state-history'] })
      setOpen(false)
    },
    // The client rule is enforced on the backend; show what it said rather than guessing.
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const missing = { name: !name.trim(), type: !type }
  const complete = !Object.values(missing).some(Boolean)
  const guidance = clientRuleHint(type, hasClient ? clientId : null)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-xl">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            if (project) {
              setTried(true)
              if (complete) save.mutate()
              return
            }
            // Creating walks four steps; each must be sound before the next opens.
            setTried(true)
            if (step === 0) {
              if (sub === 0) {
                if (missing.name || missing.type) return
                setSub(1)
                return
              }
              if (sub === 1) { setSub(2); return }
              if (!complete) { setSub(0); return }
              setStep(1)
              return
            }
            if (!complete) { setStep(0); setSub(0); return }
            setPlanTried(true)
            const problems = requiredProblems(plan)
            const here = problems.filter((p) => p.step === STEPS[step]!.id)
            if (step < STEPS.length - 1) {
              if (here.length === 0) setStep(step + 1)
              return
            }
            if (problems.length > 0) {
              const first = problems[0]!
              setStep(first.step === 'aim' ? 1 : 2)
              toast.error(`Before opening this project, add ${problems.map((p) => p.label).join(', ')}.`)
              return
            }
            save.mutate()
          }}
        >
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{project ? 'Edit project' : 'New project'}</DialogTitle>
            <DialogDescription>
              {project
                ? 'A project is one engagement: what kind of work it is, who it is for and where.'
                : step === 0
                  ? ['Let’s start with the basics: what is it called, and what kind of work is it?', 'Who is it for, and who is looking after it?', 'Anything else worth adding? All of this is optional.'][sub]
                  : 'Almost there. A few quick questions about the plan.'}
            </DialogDescription>
          </DialogHeader>

          {!project ? (
            <ol className="flex gap-1 border-b border-border px-6 py-2.5 text-tiny" aria-label="Steps">
              {STEPS.map((st, i) => (
                <li key={st.id} className="flex-1">
                  <button
                    type="button"
                    disabled={i > step}
                    onClick={() => { setStep(i); if (i === 0) setSub(0) }}
                    className={cn('flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left', i === step ? 'bg-brand-softer font-semibold text-foreground' : i < step ? 'text-text-secondary hover:bg-muted' : 'text-text-muted')}
                  >
                    <span className={cn('grid size-4 shrink-0 place-items-center rounded-full border text-[10px]', i <= step ? 'border-brand bg-brand text-white' : 'border-border')}>{i + 1}</span>
                    <span className="truncate">{st.label}</span>
                  </button>
                </li>
              ))}
            </ol>
          ) : null}

          <div className={cn('grid gap-4 overflow-y-auto px-6 py-5', !project && step > 0 && 'hidden')}>
            <div className={cn('grid gap-1.5', !showSub(0) && 'hidden')}>
              <Label htmlFor="pj-name">
                Name <Required />
              </Label>
              <Input
                id="pj-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="CNX Philippines rollout"
                maxLength={200}
                aria-invalid={tried && missing.name}
              />
              <Hint show={tried && missing.name}>Give the project a name.</Hint>
            </div>

            <div className={cn('grid gap-1.5', !showSub(0) && 'hidden')}>
              <Label>
                Type <Required />
              </Label>
              {!project ? (
                <div className="grid gap-2" role="radiogroup" aria-label="Project type">
                  {PROJECT_TYPES.filter((t) => !createTypes || createTypes.includes(t.key)).map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="radio"
                      aria-checked={type === t.key}
                      onClick={() => setType(t.key)}
                      className={cn(
                        'rounded-lg border px-3 py-2.5 text-left transition-colors dur-fast',
                        type === t.key ? 'border-brand bg-brand-soft' : 'border-border bg-card hover:bg-muted',
                      )}
                    >
                      <span className="block text-sm font-medium">{PROJECT_TYPE_LABEL[t.key]}</span>
                      <span className="block text-tiny text-text-muted">{t.hint}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {project ? (
              <Select value={type} onValueChange={(v) => setType(v as ProjectTypeKey)} disabled={project?.type === 'client'}>
                <SelectTrigger aria-invalid={tried && missing.type}>
                  {/* Label only when closed; the label + hint pair is for the open list. */}
                  <SelectValue placeholder="What kind of engagement is this?">
                    {type ? PROJECT_TYPE_LABEL[type] : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.filter((t) => project || !createTypes || createTypes.includes(t.key)).map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      <span className="flex flex-col">
                        <span>{PROJECT_TYPE_LABEL[t.key]}</span>
                        <span data-slot="item-hint" className="text-tiny text-text-muted">{t.hint}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              ) : null}
              <Hint show={tried && missing.type}>Pick a project type.</Hint>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className={cn('grid gap-1.5', !showSub(1) && 'hidden')}>
                <Label>Client</Label>
                <Select
                  value={clientId}
                  disabled={project?.type === 'client'}
                  onValueChange={(v) => {
                    setClientId(v)
                    // A geography belongs to one client, so it cannot survive the change.
                    setGeographyId(NONE)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No client</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* A project needs a client, and a client that does not exist yet should not
                    mean abandoning a half-filled project. Same answer as the project picker
                    on the work form: the way to make one belongs where the question is asked. */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 justify-self-start gap-1 px-2 text-tiny text-brand"
                  onClick={() => setCreatingClient(true)}
                >
                  <Plus className="size-3.5" />
                  Add a new client
                </Button>
              </div>

              <div className={cn('grid gap-1.5 sm:col-span-2', !showSub(1) && 'hidden')}>
                <Label>Teams involved</Label>
                <SearchSelect
                  multiple
                  value={teamIds}
                  onChange={setTeamIds}
                  options={teams.map((t) => ({ id: t.id, label: t.label }))}
                  placeholder="Search teams…"
                  pending={teamsQuery.isPending}
                  emptyHint="No teams yet — they are set up through the provisioning API."
                />
                <p className="text-tiny text-text-muted">
                  {teamIds.length === 0
                    ? 'Leave empty and any team may be given work here. Name teams to restrict it to them.'
                    : 'Work filed under this project may only be given to these teams.'}
                </p>
              </div>

              <div className={cn('sm:col-span-2', !showSub(2) && 'hidden')}>
                <SourceLinksField
                  value={sourceLinks}
                  onChange={setSourceLinks}
                  hint="The brief, the statement of work, the kickoff deck."
                />
              </div>

              {/* Opening one needs a signed URL, which only the API can mint — so the
                  existing files are a list of buttons, not links. */}
              <div className={cn('grid gap-4 sm:col-span-2', !showSub(2) && 'hidden')}>
                {project && (project.attachments?.length ?? 0) > 0 ? (
                  <AttachmentList
                    kind="project"
                    itemId={project.id}
                    attachments={project.attachments ?? []}
                  />
                ) : null}
                <AttachmentsField
                  onChange={setAttachments}
                  existing={project?.attachments ?? []}
                />
              </div>

              {/* Over the project form, not instead of it. */}
              <ClientDialog
                members={members}
                open={creatingClient}
                onOpenChange={setCreatingClient}
                onCreated={(created) => {
                  setClientId(created.id)
                  // A geography belongs to one client, so the old pick cannot survive.
                  setGeographyId(NONE)
                }}
              />

              <div className={cn('sm:col-span-2', !showSub(2) && 'hidden')}>
                <VisibilityField
                  userIds={visibleToUserIds}
                  teamIds={visibleToTeamIds}
                  onChange={({ userIds, teamIds: nextTeams }) => {
                    setVisibleToUserIds(userIds)
                    setVisibleToTeamIds(nextTeams)
                  }}
                  members={members}
                  teams={teamsQuery.data?.tree ?? []}
                />
              </div>

              <div className={cn('grid gap-1.5', !showSub(1) && 'hidden')}>
                <Label>Geography</Label>
                <Select value={geographyId} onValueChange={setGeographyId} disabled={!hasClient}>
                  <SelectTrigger>
                    <SelectValue placeholder={hasClient ? undefined : 'Pick a client first'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No geography</SelectItem>
                    {geographies.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        <span className="flex flex-col">
                          <span>
                            {g.name}
                            {g.countryCode ? (
                              <span className="ml-1 text-text-muted">· {g.countryCode}</span>
                            ) : null}
                          </span>
                          <span data-slot="item-hint" className="text-tiny text-text-muted">
                            {g.mode === 'virtual' ? 'Virtual — over a call' : (g.address ?? 'Live')}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-tiny text-text-muted">
                  {!hasClient
                    ? 'A geography qualifies a client, so it needs one first.'
                    : geographiesQuery.isPending
                      ? 'Loading geographies…'
                      : geographies.length === 0
                        ? 'This client has no geographies yet.'
                        : 'Where this engagement runs.'}
                </p>

                {hasClient ? (
                  addingGeography ? (
                    /* Inline rather than a second dialog: needing a geography that does not
                       exist yet is a normal part of opening a project, not a detour. */
                    <div className="grid gap-2 rounded-md border border-border bg-surface-sunken p-2.5">
                      <Input
                        value={geoName}
                        onChange={(e) => setGeoName(e.target.value)}
                        placeholder="CNX Philippines"
                        aria-label="Geography name"
                        className="h-8 text-xs"
                      />
                      <div className="flex gap-1.5">
                        {(['live', 'virtual'] as const).map((m) => (
                          <button
                            key={m}
                            type="button"
                            aria-pressed={geoMode === m}
                            onClick={() => setGeoMode(m)}
                            className={cn(
                              'h-8 flex-1 rounded-md border text-xs transition-colors dur-fast',
                              geoMode === m
                                ? 'border-brand bg-brand-soft font-medium text-brand-dark'
                                : 'border-border bg-card text-text-secondary hover:bg-muted',
                            )}
                          >
                            {m === 'live' ? 'Live — somewhere to go' : 'Virtual — over a call'}
                          </button>
                        ))}
                      </div>
                      {geoMode === 'live' ? (
                        <Input
                          value={geoAddress}
                          onChange={(e) => setGeoAddress(e.target.value)}
                          placeholder="Exact address — building, floor, street"
                          aria-label="Address"
                          className="h-8 text-xs"
                        />
                      ) : null}
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={!geoName.trim() || addGeography.isPending}
                          onClick={() => addGeography.mutate()}
                        >
                          {addGeography.isPending ? 'Adding…' : 'Add geography'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setAddingGeography(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddingGeography(true)}
                      className="justify-self-start text-tiny text-brand hover:underline"
                    >
                      Add a geography
                    </button>
                  )
                ) : null}
              </div>
            </div>

            <div className={cn('grid gap-1.5', !showSub(1) && 'hidden')}>
              <Label>Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Nobody yet</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <UserAvatar person={m} size="xs" />
                        {m.displayName}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className={cn('grid gap-1.5', !showSub(2) && 'hidden')}>
              <Label htmlFor="pj-description">Description</Label>
              <Textarea
                id="pj-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this engagement covers, and anything a newcomer needs to know."
                className="text-sm"
              />
            </div>

            {project && historyQuery.data ? (
              <StateHistory
                history={historyQuery.data}
                stateLabel={(state) => ({
                  empty: 'Empty',
                  ongoing: 'Ongoing',
                  blocked: 'Blocked',
                  completion_pending: 'Completion pending',
                  completed: 'Completed',
                })[state] ?? state}
                noun="state"
              />
            ) : null}

            {guidance && showSub(1) ? (
              <p className="rounded-md border border-warning bg-warning-soft px-3 py-2 text-tiny text-warning">
                {guidance}
              </p>
            ) : null}
          </div>

          {!project && step > 0 ? (
            <div className="overflow-y-auto px-6 py-5">
              <p className="mb-4 text-sm text-text-secondary">
                {step === 1 ? 'Why this project exists and how we will know it worked.' : step === 2 ? 'When it lands, how urgent it is, and the dates on the way.' : 'What is in and out, what could go wrong, and who is involved. All optional.'}
              </p>
              <PlanFields value={plan} onChange={setPlan} step={STEPS[step]!.id as 'aim' | 'timeline' | 'scope'} showErrors={planTried} />
            </div>
          ) : null}

          <DialogFooter className="border-t border-border px-6 py-4">
            {tried && !complete ? (
              <p className="mr-auto self-center text-tiny text-danger">
                Fill in the required fields.
              </p>
            ) : null}
            {!project ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (userId) {
                    saveLocalDraft<ProjectDraftPayload>({
                      id: draftId,
                      kind: 'project',
                      userId,
                      workspaceId,
                      title: name.trim() || 'Untitled project',
                      updatedAt: new Date().toISOString(),
                      payload: { name, type, clientId, geographyId, teamIds, ownerId, description, sourceLinks, attachments, visibleToUserIds, visibleToTeamIds },
                    })
                  }
                  setOpen(false)
                  onDraftClosed?.()
                  setDraftId(newDraftId())
                }}
              >
                Keep draft &amp; close
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {!project && (step > 0 || sub > 0) ? (
              <Button type="button" variant="outline" onClick={() => (step > 0 ? setStep(step - 1) : setSub(sub - 1))}>Back</Button>
            ) : null}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : project ? 'Save changes' : step === 0 && sub === 2 ? 'Next: the plan' : step < STEPS.length - 1 ? 'Next' : 'Create project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const STEPS = [
  { id: 'basics', label: 'Basics' },
  { id: 'aim', label: 'Aim & success' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'scope', label: 'Scope & risks' },
] as const

function Required() {
  return (
    <span className="text-danger" aria-hidden>
      *
    </span>
  )
}

function Hint({ show, children }: { show: boolean; children: ReactNode }) {
  return show ? <p className="text-tiny text-danger">{children}</p> : null
}
